import React, { CSSProperties, FC, useMemo } from 'react';
import clsx from 'clsx';
import { Colors } from './@types';

interface BorderInterface {
  color?: Colors;
  width?: number;
  style?: 'none' | 'hidden' | 'dotted' | 'dashed' | 'solid' | 'double'
    | 'groove' | 'ridge' | 'inset' | 'outset' | 'initial' | 'inherit';
}

type OverflowType = 'auto' | 'hidden' | 'scroll' | 'visible';

type DisplayType = 'block' | 'flex' | 'inline-block' | 'inline-flex' | 'none' | 'grid' | 'inline-grid';

type PositionType = 'relative' | 'absolute' | 'fixed';

export interface SizedBoxProps {
  backgroundColor?: Colors;
  border?: BorderInterface;
  borderRadius?: number | string;
  bottom?: number | string;
  className?: string;
  color?: Colors;
  display?: DisplayType;
  elevated?: boolean;
  flex?: number;
  fontSize?: number | string;
  height?: number | string;
  left?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  maxWidth?: number | string;
  minWidth?: number | string;
  overflow?: OverflowType;
  overflowX?: OverflowType;
  overflowY?: OverflowType;
  opacity?: number;
  position?: PositionType;
  pointerEventsNone?: boolean;
  right?: number | string;
  style?: CSSProperties;
  title?: string;
  top?: number | string;
  width?: number | string;
  zIndex?: number;
}

const SizedBox: FC<SizedBoxProps> = ({
  backgroundColor,
  border,
  borderRadius,
  bottom,
  display,
  children,
  color,
  elevated,
  flex,
  fontSize,
  height,
  left,
  minHeight,
  maxHeight,
  maxWidth,
  minWidth,
  opacity,
  overflow,
  overflowX,
  overflowY,
  position,
  pointerEventsNone,
  right,
  title,
  top,
  width,
  zIndex,
  style,
  className,
}) => {
  const finalBorder = useMemo(() => {
    if (border) {
      return {
        borderWidth: border.width || 1,
        borderStyle: border.style || 'solid',
      };
    }
    return {};
  }, [border]);

  const styles = {
    ...finalBorder,
    ...(borderRadius ? { borderRadius } : {}),
    ...(bottom ? { bottom } : {}),
    ...(height ? { height } : {}),
    ...(flex ? { flex } : {}),
    ...(fontSize ? { fontSize } : {}),
    ...(left ? { left } : {}),
    ...(minHeight ? { minHeight } : {}),
    ...(maxHeight ? { maxHeight } : {}),
    ...(minWidth ? { minWidth } : {}),
    ...(maxWidth ? { maxWidth } : {}),
    ...(opacity ? { opacity } : {}),
    ...(overflow ? { overflow } : {}),
    ...(overflowX ? { overflowX } : {}),
    ...(overflowY ? { overflowY } : {}),
    ...(top || top === 0 ? { top } : {}),
    ...(right ? { right } : {}),
    ...(width ? { width } : {}),
    ...(zIndex !== undefined ? { zIndex } : {}),
    ...style,
  };

  return (
    <div
      style={styles}
      title={title}
      className={clsx(
        backgroundColor && `bg-${backgroundColor}`,
        border && `border-${border.color || 'black'}`,
        color && `color-${color}`,
        position && `position-${position}`,
        display && `display-${display}`,
        {
          elevated,
          'pointer-events-none': pointerEventsNone,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

export default SizedBox;
